import { useArticles } from "@faire/help-center-utils/hooks/article/useArticles";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Account } from "@faire/web/slate/icons/foundation/Account";
import React from "react";
import { AccountReactivationIA } from "../instantAnswers/myAccount-accountReactivation";
import { AccountRejectedIA } from "../instantAnswers/myAccount-accountRejected";
import { BillingAndPayoutIA } from "../instantAnswers/myAccount-billingAndPayout";
import { LoginEmailIA } from "../instantAnswers/myAccount-loginEmail";
import { MultifactorAuthenticationIA } from "../instantAnswers/myAccount-multifactorAuthentication";
import { NotificationsIA } from "../instantAnswers/myAccount-notifications";
import { OnboardingHowFaireWorksIA } from "../instantAnswers/myAccount-onboardingHowFaireWorks";
import { PasswordIA } from "../instantAnswers/myAccount-password";
import { ShipFromAddressIA } from "../instantAnswers/myAccount-shipFromAddress";
import { SubmitRequestedInformationIA } from "../instantAnswers/myAccount-submitRequestedInformation";
import { TaxInformationIA } from "../instantAnswers/myAccount-taxInformation";
import { VerificationStatusIA } from "../instantAnswers/myAccount-verificationStatus";
import { accountReactivationVisibilityFn } from "../visibilityFunctions/accountReactivationVisibilityFn";
import { accountVerificationNotApprovedVisibilityFn } from "../visibilityFunctions/accountVerificationNotApprovedVisibilityFn";
import { accountVerificationNotRejectedVisibilityFn } from "../visibilityFunctions/accountVerificationNotRejectedVisibilityFn";
import { accountVerificationRejectedVisibilityFn } from "../visibilityFunctions/accountVerificationRejectedVisibilityFn";
import { brandActivePeteEventDataFn } from "./peteEventDataFunction/brandActivePeteEventDataFn";
export var useMyAccountBrandNodes = function useMyAccountBrandNodes() {
  var _useStrictLocalizatio = useStrictLocalization(),
    strictLocalize = _useStrictLocalizatio.strictLocalize;
  var articles = useArticles();
  return {
    MyAccount: {
      id: "MyAccount",
      label: strictLocalize({
        id: "uB52R5",
        defaultMessage: "My account"
      }),
      icon: /*#__PURE__*/React.createElement(Account, {
        fontSize: "24px",
        color: "faireBlack"
      }),
      children: ["UpdateAccountInformation", "LoginCredentials", "Onboarding"],
      level: 1,
      sidebarTitle: strictLocalize({
        id: "C26nWc",
        defaultMessage: "Account issue"
      })
    },
    UpdateAccountInformation: {
      id: "UpdateAccountInformation",
      label: strictLocalize({
        id: "ZMYKRy",
        defaultMessage: "Update account information"
      }),
      children: ["BillingAndPayout", "Notifications", "ShipFromAddress", "TaxInformation", "AccountReactivation"],
      level: 2
    },
    LoginCredentials: {
      id: "LoginCredentials",
      label: strictLocalize({
        id: "GgWCjj",
        defaultMessage: "Login credentials"
      }),
      children: ["MultifactorAuthentication", "LoginEmail", "Password"],
      level: 2
    },
    Onboarding: {
      id: "Onboarding",
      label: strictLocalize({
        id: "g2qNGE",
        defaultMessage: "Onboarding"
      }),
      children: ["OnboardingHowFaireWorks", "AccountVerification"],
      additionalPeteEventData: brandActivePeteEventDataFn,
      level: 2
    },
    BillingAndPayout: {
      id: "BillingAndPayout",
      label: strictLocalize({
        id: "wnLucE",
        defaultMessage: "Billing and payout"
      }),
      instantAnswer: BillingAndPayoutIA(strictLocalize),
      level: 3
    },
    Notifications: {
      id: "Notifications",
      label: strictLocalize({
        id: "biWW5s",
        defaultMessage: "Notifications"
      }),
      articles: [articles.Account.EMAIL_PREFERENCES],
      instantAnswer: NotificationsIA(strictLocalize),
      level: 3
    },
    ShipFromAddress: {
      id: "ShipFromAddress",
      label: strictLocalize({
        id: "todvNV",
        defaultMessage: "Ship From Address"
      }),
      instantAnswer: ShipFromAddressIA(strictLocalize),
      level: 3
    },
    TaxInformation: {
      id: "TaxInformation",
      label: strictLocalize({
        id: "y/STqM",
        defaultMessage: "Tax information"
      }),
      articles: [articles.InvoicesAndBilling.TAX_INFORMATION],
      instantAnswer: TaxInformationIA(strictLocalize),
      level: 3
    },
    AccountReactivation: {
      id: "AccountReactivation",
      label: strictLocalize({
        id: "09uqMp",
        defaultMessage: "Account reactivation"
      }),
      instantAnswer: AccountReactivationIA(strictLocalize),
      isNodeVisible: accountReactivationVisibilityFn,
      level: 3
    },
    MultifactorAuthentication: {
      id: "MultifactorAuthentication",
      label: strictLocalize({
        id: "k8NE3+",
        defaultMessage: "Multifactor authentication"
      }),
      instantAnswer: MultifactorAuthenticationIA(strictLocalize),
      level: 3
    },
    LoginEmail: {
      id: "LoginEmail",
      label: strictLocalize({
        id: "/ULOia",
        defaultMessage: "Login email"
      }),
      articles: [articles.Account.ACCOUNT_EMAIL],
      instantAnswer: LoginEmailIA(strictLocalize),
      level: 3
    },
    Password: {
      id: "Password",
      label: strictLocalize({
        id: "dszgKL",
        defaultMessage: "Password"
      }),
      articles: [articles.Account.RESETTING_PASSWORD],
      instantAnswer: PasswordIA(strictLocalize),
      level: 3
    },
    OnboardingHowFaireWorks: {
      id: "OnboardingHowFaireWorks",
      label: strictLocalize({
        id: "iefDE6",
        defaultMessage: "How Faire works"
      }),
      articles: [articles.FAQForProspectiveBrands.BECOME_BRAND],
      instantAnswer: OnboardingHowFaireWorksIA(strictLocalize),
      level: 3
    },
    AccountVerification: {
      id: "AccountVerification",
      label: strictLocalize({
        id: "d3IQBe",
        defaultMessage: "Account verification"
      }),
      children: ["VerificationStatus", "SubmitRequestedInformation", "AccountRejected"],
      isNodeVisible: accountVerificationNotApprovedVisibilityFn,
      level: 3
    },
    VerificationStatus: {
      id: "VerificationStatus",
      label: strictLocalize({
        id: "UgVUFf",
        defaultMessage: "Verification status"
      }),
      instantAnswer: VerificationStatusIA(strictLocalize),
      isNodeVisible: accountVerificationNotRejectedVisibilityFn,
      level: 4
    },
    SubmitRequestedInformation: {
      id: "SubmitRequestedInformation",
      label: strictLocalize({
        id: "qVpwX1",
        defaultMessage: "Submit requested information"
      }),
      instantAnswer: SubmitRequestedInformationIA(strictLocalize),
      isNodeVisible: accountVerificationNotRejectedVisibilityFn,
      level: 4
    },
    AccountRejected: {
      id: "AccountRejected",
      label: strictLocalize({
        id: "I0WFaX",
        defaultMessage: "Account rejected"
      }),
      instantAnswer: AccountRejectedIA(strictLocalize),
      isNodeVisible: accountVerificationRejectedVisibilityFn,
      level: 4
    }
  };
};